import { Permissions } from "@cnx/common";
import {
  IconAlertCircle,
  IconBulb,
  IconClockPlay,
  IconClockSearch,
  IconMail,
  IconNews,
  IconStethoscope,
  IconTruck,
} from "@tabler/icons-react";
import FromShipIcon from "assets/icons/shipment/from-ship.svg";
import ToShipIcon from "assets/icons/shipment/to-ship.svg";
import {
  FEATURE_TERMINAL_MANAGEMENT,
  FEATURE_TERMINAL_UNCLEAR_SHIPMENTS,
} from "common/constants";
import { useFeatureSupport } from "hooks/useFeatureSupport";
import IconHaileybirdh from "icons/common/haileybirdh.svg";
import IconUnclearShipment from "icons/shipment/unclear-shipment.svg";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "stores/authStore";

export interface QuickLink {
  label: string;
  description?: string;
  icon: React.ReactNode;
  link: string;
  requiredRights: Permissions[];
}

export const useQuickActions = () => {
  const { t } = useTranslation("common");
  const { t: tNews } = useTranslation("news");
  const useRights = new Set(useAuthStore(state => state.useRights));
  const { isFeatureEnabled } = useFeatureSupport();

  const hasRight = (...rights: Permissions[]) =>
    rights.some(right => useRights.has(right));

  const quickLinks: QuickLink[] = [];

  hasRight(
    Permissions.RIGHTS_SHIPMENTS_USER,
    Permissions.RIGHTS_SHIPMENTS_ADMIN
  ) &&
    quickLinks.push({
      label: t("shipments"),
      icon: <IconTruck className="text-blue" />,
      link: "/trips",
      requiredRights: [
        Permissions["RIGHTS_SHIPMENTS_USER"],
        Permissions["RIGHTS_SHIPMENTS_ADMIN"],
      ],
    });

  hasRight(
    Permissions.RIGHTS_USER_STORIES_USER,
    Permissions.RIGHTS_USER_STORIES_ADMIN
  ) &&
    quickLinks.push({
      label: t("ideas"),
      icon: <IconBulb className="text-blue" />,
      link: "/epics",
      requiredRights: [
        Permissions["RIGHTS_USER_STORIES_USER"],
        Permissions["RIGHTS_USER_STORIES_ADMIN"],
      ],
    });

  hasRight(Permissions.RIGHTS_EXCEPTIONS_ADMIN) &&
    quickLinks.push({
      label: t("damages"),
      icon: <IconAlertCircle className="text-blue" />,
      link: "/exceptions",
      requiredRights: [Permissions["RIGHTS_EXCEPTIONS_ADMIN"]],
    });

  const terminalLinks: QuickLink[] = [];

  hasRight(
    Permissions.RIGHTS_TERMINAL_USER,
    Permissions.RIGHTS_TERMINAL_ADMIN
  ) &&
    isFeatureEnabled(FEATURE_TERMINAL_MANAGEMENT) &&
    terminalLinks.push(
      {
        label: t("terminalArrivalsShort"),
        description: t("terminal").toUpperCase(),
        icon: <FromShipIcon className="text-blue" width={24} height={24} />,
        link: "/terminal-management/arrivals",
        requiredRights: [
          Permissions["RIGHTS_TERMINAL_USER"],
          Permissions["RIGHTS_TERMINAL_ADMIN"],
        ],
      },
      {
        label: t("terminalDeparturesShort"),
        description: t("terminal").toUpperCase(),
        icon: <ToShipIcon className="text-blue" width={24} height={24} />,
        link: "/terminal-management/departures",
        requiredRights: [
          Permissions["RIGHTS_TERMINAL_USER"],
          Permissions["RIGHTS_TERMINAL_ADMIN"],
        ],
      }
    );

  hasRight(
    Permissions.RIGHTS_TERMINAL_USER,
    Permissions.RIGHTS_TERMINAL_ADMIN
  ) &&
    isFeatureEnabled(FEATURE_TERMINAL_UNCLEAR_SHIPMENTS) &&
    terminalLinks.push({
      label: t("terminalUnclearShort"),
      description: t("terminal").toUpperCase(),
      icon: (
        <IconUnclearShipment className="text-blue" width={24} height={24} />
      ),
      link: "/terminal-management/unclear-shipments",
      requiredRights: [
        Permissions["RIGHTS_TERMINAL_USER"],
        Permissions["RIGHTS_TERMINAL_ADMIN"],
      ],
    });

  const externalLinks: QuickLink[] = [
    {
      label: tNews("logHours"),
      description: "JYLHÄNET",
      icon: <IconClockPlay className="text-yellow" />,
      link: "https://tyoajanseuranta.jylhanet.fi/login",
      requiredRights: [Permissions["RIGHTS_HOUR_REPORTING"]],
    },
    {
      label: tNews("checkHours"),
      description: "JYLHÄNET",
      icon: <IconClockSearch className="text-yellow" />,
      link: "https://tyoajanseuranta.jylhanet.fi/backend#/login",
      requiredRights: [Permissions["RIGHTS_HOUR_REPORTING"]],
    },
    {
      label: tNews("newsLetter"),
      description: "JYLHÄNET",
      icon: <IconNews className="text-yellow" />,
      link: "https://www.jylhanet.fi/",
      requiredRights: [Permissions["RIGHTS_NEWSLETTER"]],
    },
    {
      label: "Outlook",
      icon: <IconMail className="text-yellow" />,
      link: "https://outlook.office365.com/",
      requiredRights: [Permissions["RIGHTS_OUTLOOK"]],
    },
  ].filter(action => {
    if (!action.requiredRights) return true;
    return action.requiredRights.some(right => useRights.has(right));
  });

  const bottomExternalLinks: QuickLink[] = [
    {
      label: "HAILEY HR",
      icon: <IconHaileybirdh className="fill-red" width={24} height={24} />,
      link: "https://login.haileyhr.app/Account/Login",
      requiredRights: [Permissions["RIGHTS_HAILEY_HR"]],
    },
    {
      label: "Pihlajalinna",
      icon: <IconStethoscope className="text-red" width={24} height={24} />,
      link: "https://www.pihlajalinna.fi/ajanvaraus/asiakkuus/tyoterveys",
      requiredRights: [Permissions["RIGHTS_PIHLAJALINNA"]],
    },
  ].filter(action => {
    if (!action.requiredRights) return true;
    return action.requiredRights.some(right => useRights.has(right));
  });

  return [
    quickLinks,
    terminalLinks,
    externalLinks,
    bottomExternalLinks,
  ] as const;
};
